import { Autocomplete, Grid } from '@mui/material'
import { AppBox, AppIcon, AppTypography, SimpleCheckBox, SimpleField, SimpleValueSelect } from 'components'
import { fieldColumnMapping, fieldMapping, fieldsMapping } from 'hooks/crm/import-crm-contact.hook'
import { Dispatch, FC, SetStateAction, useCallback } from 'react'

interface Props {
  fieldMappings: fieldMapping
  setFieldMappings: Dispatch<SetStateAction<fieldMapping>>
  fileHeaders: any[]
  isCompanyNotIncluded: boolean
  setIsCompanyNotIncluded: Dispatch<SetStateAction<boolean>>
}

export const ImportContactWizardStepThreeMapping: FC<Props> = ({
  fieldMappings,
  setFieldMappings,
  fileHeaders,
  isCompanyNotIncluded,
  setIsCompanyNotIncluded,
}) => {
  const showableHeaders = useCallback(
    (x: fieldColumnMapping) => {
      return fileHeaders
        .filter((z) => !fieldMappings.contacts.find((y) => y.column === z && y.field !== x.field))
        .filter((z) => !fieldMappings.companies.find((y) => y.column === z && y.field !== x.field))
        .filter((z) => fieldMappings.contactTypeColumn !== z)
        .map((x) => {
          return { value: x, label: x }
        })
    },
    [fileHeaders, fieldMappings]
  )
  return (
    <Grid container spacing={2} alignItems={'stretch'} height={'60vh'}>
      <Grid item xs={12}>
        <AppBox backgroundColor="white" padding={2} borderRadius={3} border={1} borderColor={'grey.300'}>
          <AppTypography color={'grey.500'} fontSize={14} fontWeight={400}>
            Now that we know how to organize your contacs, please map all of the remaining fields that should be
            imported into your CRM. Each field within your file can only be mapped once.
          </AppTypography>
        </AppBox>
      </Grid>
      <Grid item xs={6}>
        <AppBox backgroundColor="white" paddingY={2} paddingX={4} borderRadius={3} border={1} borderColor={'grey.300'}>
          <AppBox borderBottom={1} borderColor={'grey.300'} paddingBottom={1}>
            <AppTypography fontSize={14} fontWeight={500}>
              Contact Records
            </AppTypography>
          </AppBox>
          <Grid container alignItems={'center'} rowGap={2} paddingTop={2}>
            {fieldMappings.contacts.map((x) => (
              <>
                <Grid item xs={3}>
                  <AppBox display={'flex'} alignItems={'center'} gap={1}>
                    <AppTypography fontSize={14} fontWeight={600} color={'grey.500'}>
                      {x.field.Title}
                    </AppTypography>
                    {x.field.isRequired && <AppTypography color={'red'}>*</AppTypography>}
                  </AppBox>
                </Grid>
                <Grid item xs={3}>
                  <AppBox display={'flex'} gap={1}>
                    <AppTypography fontWeight={600} color={'grey.500'}>
                      maps to
                    </AppTypography>
                    <AppIcon size="medium" icon="tabler:arrow-right" color={'#64748B'} />
                  </AppBox>
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    popupIcon={<AppIcon size="large" icon="mdi:chevron-down" />}
                    size="small"
                    disablePortal={true}
                    options={showableHeaders(x)}
                    onChange={(event, value) => {
                      setFieldMappings((prev) => {
                        const prevContacts = JSON.parse(JSON.stringify(prev.contacts)) as fieldsMapping
                        prevContacts.find((y) => y.field.Title === x.field.Title)!.column = value?.value
                        return { ...prev, contacts: prevContacts }
                      })
                    }}
                    value={x.column ? { value: x.column, label: x.column } : null}
                    renderInput={(params) => (
                      <SimpleField
                        style={{ marginTop: 0 }}
                        {...params}
                        ref={params.InputProps.ref}
                        margin="dense"
                        placeholder="Ignore field"
                      />
                    )}
                  />
                </Grid>
              </>
            ))}
          </Grid>
        </AppBox>
      </Grid>
      <Grid item xs={6}>
        <AppBox backgroundColor="white" paddingY={2} paddingX={4} borderRadius={3} border={1} borderColor={'grey.300'}>
          <AppBox
            borderBottom={1}
            borderColor={'grey.300'}
            paddingBottom={1}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <AppTypography fontSize={14} fontWeight={500}>
              Company Records
            </AppTypography>
            <SimpleCheckBox
              size="small"
              checked={isCompanyNotIncluded}
              onClick={() => {
                setFieldMappings((prev) => {
                  const prevContacts = JSON.parse(JSON.stringify(prev.companies)) as fieldsMapping
                  prevContacts.forEach((y) => (y.column = null))
                  return { ...prev, companies: prevContacts }
                })
                setIsCompanyNotIncluded(!isCompanyNotIncluded)
              }}
              label="Exclude All"
              style={{ padding: 0, paddingRight: 2 }}
            />
          </AppBox>
          <Grid container alignItems={'center'} rowGap={2} paddingTop={2}>
            {fieldMappings.companies.map((x) => (
              <>
                <Grid item xs={3}>
                  <AppBox display={'flex'} alignItems={'center'} gap={1}>
                    <AppTypography fontSize={14} fontWeight={600} color={'grey.500'}>
                      {x.field.Title}
                    </AppTypography>
                    {x.field.isRequired && <AppTypography color={'red'}>*</AppTypography>}
                  </AppBox>
                </Grid>
                <Grid item xs={3}>
                  <AppBox display={'flex'} gap={1}>
                    <AppTypography fontWeight={600} color={'grey.500'}>
                      maps to
                    </AppTypography>
                    <AppIcon size="medium" icon="tabler:arrow-right" color={'#64748B'} />
                  </AppBox>
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    popupIcon={<AppIcon size="large" icon="mdi:chevron-down" />}
                    size="small"
                    disablePortal={true}
                    options={showableHeaders(x)}
                    onChange={(event, value) => {
                      setFieldMappings((prev) => {
                        const prevContacts = JSON.parse(JSON.stringify(prev.companies)) as fieldsMapping
                        prevContacts.find((y) => y.field.Title === x.field.Title)!.column = value?.value
                        return { ...prev, companies: prevContacts }
                      })
                    }}
                    value={x.column ? { value: x.column, label: x.column } : null}
                    renderInput={(params) => (
                      <SimpleField
                        style={{ marginTop: 0 }}
                        {...params}
                        ref={params.InputProps.ref}
                        margin="dense"
                        placeholder="Ignore field"
                      />
                    )}
                  />
                </Grid>
              </>
            ))}
          </Grid>
        </AppBox>
      </Grid>
    </Grid>
  )
}
